import { memo, useCallback, useMemo, useState } from 'react';
import { Pressable, type LayoutChangeEvent } from 'react-native';

import { imgUser } from '@/assets/images';
import { Stack } from '@/components/layout';
import { Avatar, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { BottomSideBorder } from '@/features/guardian/components/bottom-side-border';
import type { FamilyKid } from '@/features/guardian/utils/family-kids';
import { levelColor } from '@/features/onboarding/utils/player-card';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

const strings = STRINGS.guardian.kids;

export type FamilyKidCardProps = {
  kid: FamilyKid;
  onPress?: (playerId: string) => void;
};

const AVATAR_SIZE = 60;
const PROGRESS_TRACK_HEIGHT = 6;
const LEVEL_BORDER_EDGE_WIDTH = 4;
const LEVEL_BORDER_TIP_WIDTH = 0;
const LEVEL_BORDER_STEPS = 24;

const clampProgress = (progress: number) => Math.max(0, Math.min(1, progress));

function FamilyKidCard({ kid, onPress }: FamilyKidCardProps) {
  const { isTablet, scale } = useScaling();
  const styles = useStyles();

  const color = useMemo(() => levelColor(kid.level), [kid.level]);
  const normalizedProgress = clampProgress(kid.progress);
  const percent = Math.round(normalizedProgress * 100);

  const avatarSource = kid.avatar ?? imgUser;
  const hasAvatar = Boolean(kid.avatar);
  const [measuredSize, setMeasuredSize] = useState({ width: 0, height: 0 });

  const metaLabel = useMemo(() => {
    const pins = strings.pins
      .replace('{earned}', String(kid.pinsEarned))
      .replace('{total}', String(kid.pinsTotal));
    const next = strings.next.replace('{time}', kid.nextLabel);
    return `${pins} · ${next}`;
  }, [kid.pinsEarned, kid.pinsTotal, kid.nextLabel]);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    setMeasuredSize(previousSize =>
      previousSize.width === width && previousSize.height === height
        ? previousSize
        : { width, height },
    );
  }, []);

  const handlePress = useCallback(() => {
    onPress?.(kid.id);
  }, [kid.id, onPress]);

  const levelLabel = STRINGS.guardian.home.levelBadge.replace('{level}', String(kid.level));

  const avatar = (
    <Avatar size={scale(AVATAR_SIZE)} borderWidth={1} borderColor={colors['3']} activeOpacity={1}>
      <Avatar.Image
        source={avatarSource}
        size={hasAvatar ? undefined : scale(AVATAR_SIZE)}
        disableCircular={!hasAvatar}
        backgroundColor={colors['1']}
      />
    </Avatar>
  );

  const nameText = (
    <Text
      numberOfLines={isTablet ? 1 : undefined}
      variant="Font Guardian App/Body/Body 16 Bold"
      style={styles.name}>
      {kid.name}
    </Text>
  );

  const ageBadge =
    kid.age !== undefined ? (
      <Stack style={styles.ageBadge}>
        <Text
          numberOfLines={1}
          variant="Font Guardian App/Subtitle/Subtitle 11 Semibold"
          style={styles.ageBadgeText}>
          {strings.ageBadge.replace('{age}', String(kid.age))}
        </Text>
      </Stack>
    ) : null;

  const metaText = (
    <Text
      numberOfLines={1}
      variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
      style={styles.meta}>
      {metaLabel}
    </Text>
  );

  const levelBadge = (
    <Stack
      direction="row"
      align="center"
      gap={scale(6)}
      style={[styles.levelBadge, { backgroundColor: color }]}>
      <Text
        numberOfLines={1}
        variant="Font Guardian App/Subtitle/Subtitle 10 Semibold"
        style={styles.levelBadgeText}>
        {levelLabel}
      </Text>
      {isTablet ? (
        <>
          <Text
            numberOfLines={1}
            variant="Font Guardian App/Subtitle/Subtitle 10 Semibold"
            style={styles.levelBadgeText}>
            •
          </Text>
          <Text
            numberOfLines={1}
            variant="Font Guardian App/Subtitle/Subtitle 12 Semibold"
            style={styles.levelBadgeText}>
            {percent}%
          </Text>
        </>
      ) : null}
    </Stack>
  );

  const progressTrack = (
    <Stack
      height={scale(PROGRESS_TRACK_HEIGHT)}
      borderRadius={scale(999)}
      style={[styles.track, isTablet ? null : styles.mobileTrack]}>
      <Stack
        height="100%"
        borderRadius={scale(999)}
        backgroundColor={color}
        width={`${percent}%`}
      />
    </Stack>
  );

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={kid.name}
      disabled={!onPress}
      onPress={onPress ? handlePress : undefined}
      style={({ pressed }) => [
        styles.pressable,
        isTablet ? styles.tabletPressable : null,
        pressed && onPress ? styles.cardPressed : null,
      ]}>
      <Stack
        onLayout={isTablet ? handleLayout : undefined}
        style={[styles.surface, isTablet ? styles.tabletSurface : null]}>
        <Stack
          flexGrow={isTablet ? 1 : undefined}
          borderRadius={scale(12)}
          paddingLeft={scale({ phone: 12, tablet: 20 })}
          paddingRight={scale({ phone: 12, tablet: 20 })}
          paddingTop={scale({ phone: 12, tablet: 10 })}
          paddingBottom={scale({ phone: 16, tablet: 10 })}
          justify={isTablet ? 'center' : undefined}
          backgroundColor={colors['0']}>
          {isTablet ? (
            // ── Tablet: avatar + three-row detail (name, progress, level/meta) ───
            <Stack direction="row" align="center" gap={scale(16)}>
              {avatar}
              <Stack flex={1} minWidth={0} gap={scale(12)}>
                {/* Name + age */}
                <Stack direction="row" align="center" gap={scale(8)}>
                  {nameText}
                  {ageBadge}
                </Stack>

                {/* Progress track spans the detail column */}
                {progressTrack}

                {/* Level pill + pins/next */}
                <Stack direction="row" align="center" justify="space-between" gap={scale(12)}>
                  {levelBadge}
                  <Stack flexShrink={1} align="flex-end">
                    {metaText}
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          ) : (
            // ── Mobile: avatar + name/meta, then level + percent, then track ─────
            <>
              <Stack direction="row" align="center" gap={scale(12)}>
                {avatar}
                <Stack flex={1} gap={scale(8)}>
                  <Stack direction="row" align="center" gap={scale(4)} flex={1}>
                    {nameText}
                    {ageBadge}
                  </Stack>
                  {metaText}
                </Stack>
              </Stack>

              <Stack direction="row" align="center" justify="space-between" marginTop={scale(8)}>
                {levelBadge}
                <Text
                  numberOfLines={1}
                  variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
                  style={styles.percent}>
                  {percent} %
                </Text>
              </Stack>
              {progressTrack}
            </>
          )}
        </Stack>

        {isTablet ? (
          <BottomSideBorder
            color={color}
            edgeWidth={LEVEL_BORDER_EDGE_WIDTH}
            height={measuredSize.height}
            radius={scale(12)}
            side="left"
            steps={LEVEL_BORDER_STEPS}
            tipWidth={LEVEL_BORDER_TIP_WIDTH}
            width={measuredSize.width}
          />
        ) : null}
      </Stack>
    </Pressable>
  );
}

const useStyles = createStyles(({ scale }) => ({
  pressable: {
    width: '100%',
    borderRadius: scale(12),
    backgroundColor: '#FFFFFF',
    shadowColor: '#0F225B',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.05,
    shadowRadius: 12,
    elevation: 2,
  },
  cardPressed: {
    opacity: 0.72,
  },
  tabletPressable: {
    // flexGrow (not flex) keeps flexBasis: 'auto', so the card is sized by its
    // content and then grows to match the tallest card in the row.
    flexGrow: 1,
  },
  surface: {
    width: '100%',
    borderRadius: scale(12),
    overflow: 'hidden',
  },
  tabletSurface: {
    flexGrow: 1,
  },
  name: {
    color: guardianColors['10'],
    flexShrink: 1,
  },
  ageBadge: {
    paddingHorizontal: scale(12),
    paddingVertical: scale(3),
    borderRadius: scale(999),
    borderWidth: 1,
    borderColor: guardianColors['3'],
    alignItems: 'center',
    justifyContent: 'center',
    // The name truncates before the age pill gives up any width.
    flexShrink: 0,
  },
  ageBadgeText: {
    color: guardianColors['10'],
  },
  meta: {
    color: guardianColors['6'],
  },
  levelBadge: {
    paddingHorizontal: scale(8),
    borderRadius: scale(999),
    paddingVertical: scale(4),
  },
  levelBadgeText: {
    color: colors['0'],
  },
  track: {
    backgroundColor: guardianColors['3'],
    overflow: 'hidden',
  },
  mobileTrack: {
    marginTop: scale(4),
  },
  percent: {
    color: guardianColors['10'],
  },
}));

export default memo(FamilyKidCard);
